import { Account } from '../types/Account';
import { Transaction } from '../types/Transaction';
import { Summary } from '../types/Summary';
import { useAxiosWithAuth } from '../utils/api';

export const useApi = () => {
  const axiosInstance = useAxiosWithAuth();

  const fetchProtectedResource = async (): Promise<any> => {
    try {
      const response = await axiosInstance.get('/protected');
      return response.data;
    } catch (error) {
      console.error('Error fetching protected resource:', error);
      throw error;
    }
  };

  const getFinancialDetails = async (): Promise<any> => {
    try {
      const response = await axiosInstance.get('/akahu/financial-details');
      // return sample_api_response;
      return response.data;
    } catch (error) {
      console.error('Error fetching financial details:', error);
      throw error;
    }
  };

  const getAccounts = async (): Promise<Account[]> => {
    try {
      const response = await axiosInstance.get('/accounts');
      return response.data;
    } catch (error) {
      console.error('Error fetching accounts:', error);
      throw error;
    }
  };

  const setAccountRemoved = async (
    accountId: string,
    removed: boolean,
  ): Promise<void> => {
    try {
      await axiosInstance.patch(`/accounts/${accountId}`, { removed });
    } catch (error) {
      console.error('Error updating account:', error);
      throw error;
    }
  };

  const createManualAccount = async (account: {
    name: string;
    type: string;
    currency: string;
    balance: number;
  }): Promise<void> => {
    try {
      await axiosInstance.post('/accounts', account);
    } catch (error) {
      console.error('Error creating account:', error);
      throw error;
    }
  };

  const convertToManualAccount = async (accountId: string): Promise<void> => {
    try {
      await axiosInstance.patch(`/accounts/${accountId}`, { manual: true });
    } catch (error) {
      console.error('Error converting account:', error);
      throw error;
    }
  };

  const updateManualBalance = async (
    accountId: string,
    current: number,
  ): Promise<void> => {
    try {
      await axiosInstance.put(`/accounts/${accountId}/balance`, { current });
    } catch (error) {
      console.error('Error updating balance:', error);
      throw error;
    }
  };

  const getTransactions = async (page?: number): Promise<any> => {
    try {
      console.log('Fetching transactions from API...');
      const response = await axiosInstance.get('/akahu/transactions', {
        params: {
          page,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  };

  const getFinancialSummary = async (): Promise<{
    items: Summary[];
    recordDateNz: string | null;
  }> => {
    try {
      console.log('Fetching financial summary from API...');
      const response = await axiosInstance.get('/accounts/total-balance');

      return {
        items: [
          {
            name: 'Total Balance',
            amount: Number(response.data?.totalBalance ?? 0),
            recordDateNz: response.data?.recordDateNz ?? null,
          },
        ],
        recordDateNz: response.data?.recordDateNz ?? null,
      };
    } catch (error) {
      console.error('Error fetching financial summary:', error);
      throw error;
    }
  };

  const refreshTransactions = async (): Promise<any> => {
    try {
      console.log('Refreshing transactions from API...');
      const response = await axiosInstance.get('/akahu/transaction-updates');
      return response.data;
    } catch (error) {
      console.error('Error refreshing transactions:', error);
      throw error;
    }
  };

  const getMonthlyExpenses = async (): Promise<any> => {
    try {
      const response = await axiosInstance.get('/akahu/monthly-expenses');
      return response.data;
    } catch (error) {
      console.error('Error fetching monthly expenses:', error);
      throw error;
    }
  };

  const getTransactionsByCategory = async (category: string): Promise<any> => {
    try {
      const response = await axiosInstance.post(
        '/akahu/transactions-by-category',
        {
          categoryName: category,
        },
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching transactions for category ${category}:`,
        error,
      );
      throw error;
    }
  };

  const createRelatedTransaction = async (
    parentTransactionId: string,
    relatedTransactionId: string,
    relationType: string = 'reimbursement',
  ): Promise<void> => {
    try {
      const response = await axiosInstance.post('/akahu/related-transactions', {
        parentTransactionId,
        relatedTransactionId,
        relationType,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating related transaction:', error);
      throw new Error(
        'Failed to create related transaction. Please try again later.',
      );
    }
  };

  const getAssetAllocation = async (): Promise<any[]> => {
    try {
      const response = await axiosInstance.get('/assets/allocation');
      return response.data;
    } catch (error) {
      console.error('Error fetching asset allocation:', error);
      throw error;
    }
  };

  const syncAssets = async (): Promise<{ message: string }> => {
    try {
      const response = await axiosInstance.put('/assets/sync');
      return response.data;
    } catch (error) {
      console.error('Error syncing assets:', error);
      throw error;
    }
  };

  const fetchCreditTransactions = async (
    page: number,
  ): Promise<Transaction[]> => {
    try {
      const response = await axiosInstance.get('/akahu/credit-transactions', {
        params: { page },
      });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching credit transactions:', error);
      throw new Error(
        'Failed to fetch credit transactions. Please try again later.',
      );
    }
  };

  return {
    fetchProtectedResource,
    getFinancialDetails,
    getAccounts,
    setAccountRemoved,
    createManualAccount,
    convertToManualAccount,
    updateManualBalance,
    getFinancialSummary,
    getTransactions,
    refreshTransactions,
    getMonthlyExpenses,
    getTransactionsByCategory,
    createRelatedTransaction,
    fetchCreditTransactions,
    getAssetAllocation,
    syncAssets,
  };
};
